/**
 * Featurebase integration (feedback tab, What's new popup, messenger,
 * surveys).
 *
 * The Provider runs in every env listed in FEATUREBASE_ENABLED_ENVS; each
 * env's App ID decides which workspace it talks to. The floating feedback
 * tab is currently hidden in every env (FEATUREBASE_FEEDBACK_ENABLED_ENVS
 * is empty) — Marker.io covers the non-production envs via
 * ProtectedRoute.tsx. See ./env.ts for the environment classification rules.
 *
 * The Provider boots the SDK but does not auto-mount any widget — each needs
 * an explicit hook call, which is why the *Mount components exist.
 *
 * What's new: the changelog widget is mounted once here (popup only, never
 * auto-opening, no dropdown or floating card) and exposed through
 * FeaturebaseChangelogContext. Components never call featurebase-js
 * directly, so Featurebase's script is only loaded where it is enabled.
 *
 * Surveys: the survey runtime is mounted once here. Featurebase then shows
 * each live survey by itself on the pages its dashboard rules target (e.g.
 * order confirmation). The workspace needs Outbound (where surveys live)
 * switched on. The featurebase-js docs say surveys run without the
 * messenger, but the dashboard's survey editor asks for the Support runtime
 * too — confirm on a build before relying on Support being off.
 *
 * Messenger: the Provider boots it when the workspace has Support switched
 * on. Its floating bubble is hidden (hideDefaultLauncher) in every env —
 * engineers open it from the header MessagesButton instead. Components check
 * FeaturebaseAvailableContext before calling messenger hooks.
 *
 * User identity verification uses a backend-signed JWT fetched via
 * useFeaturebaseJwt(). If the JWT request fails (backend down, env not
 * configured), the Provider mounts anonymous and the widget falls back to
 * its built-in sign-in prompt — never breaks the rest of the app.
 *
 * See: https://help.featurebase.app/articles/5257986-creating-and-signing-a-jwt
 */
import {
  FeaturebaseProvider,
  useChangelogWidget,
  useFeedbackWidget,
  useSurveyWidget,
} from 'featurebase-js/react'
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useFeaturebaseJwt } from '../auth/hooks/useFeaturebaseJwt'
import { useChangelogRefreshKey } from '../hooks/useChangelogRefreshKey'
import { useUnreadUpdatesTracker } from '../hooks/useUnreadChangelog'
import { getAppEnv } from './env'
import {
  FeaturebaseAvailableContext,
  FeaturebaseChangelogContext,
  FeaturebaseUnreadUpdatesContext,
} from './featurebase-context'
import type { FeaturebaseChangelogApi } from './featurebase-context'
import {
  isFeaturebaseEnabled,
  isFeaturebaseFeedbackEnabled,
} from './feedback-widgets'

// Module-level so the hook sees the same config every render — it rebuilds
// the widget whenever its config changes. Popup only: the engineer opens it
// from the Home "See what's new" button; it must never open by itself.
const CHANGELOG_WIDGET_CONFIG = {
  theme: 'light',
  popup: { enabled: true, autoOpenForNewUpdates: false },
  dropdown: { enabled: false, lazyLoad: true },
  changelogCard: { enabled: false },
} as const

interface ConditionalFeaturebaseProviderProps {
  children: ReactNode
}

function FeedbackWidgetMount() {
  useFeedbackWidget({
    theme: 'light',
    // Side-attached on the right edge (vertical tab) so it doesn't collide
    // with the engineer mobile bottom nav. Matches Marker.io's placement so
    // the two widgets feel consistent across environments.
    placement: 'right',
  })
  return null
}

function SurveyWidgetMount() {
  // Bottom-right, matching the messenger window. The runtime has no
  // teardown, so this is mounted once and left running.
  useSurveyWidget({ placement: 'bottom-right' })
  return null
}

interface ChangelogWidgetMountProps {
  onReady: (api: FeaturebaseChangelogApi) => void
}

function ChangelogWidgetMount({ onReady }: ChangelogWidgetMountProps) {
  const widget = useChangelogWidget(CHANGELOG_WIDGET_CONFIG)
  // The hook's functions may change identity between renders. Hand out
  // stable wrappers once and route them to the latest functions via a ref,
  // so publishing the API can't trigger a re-render loop.
  const widgetRef = useRef(widget)
  useEffect(() => {
    widgetRef.current = widget
  })

  useEffect(() => {
    onReady({
      openPopup: () => widgetRef.current.openPopup(),
      markAllAsViewed: () => widgetRef.current.markAllAsViewed(),
      getUnviewedCount: () => widgetRef.current.getUnviewedCount(),
    })
  }, [onReady])

  return null
}

export function ConditionalFeaturebaseProvider({
  children,
}: ConditionalFeaturebaseProviderProps) {
  const appId = import.meta.env.VITE_FEATUREBASE_APP_ID
  const { data: jwtResponse, isFetched: isJwtFetched } = useFeaturebaseJwt()
  const [changelogApi, setChangelogApi] =
    useState<FeaturebaseChangelogApi | null>(null)
  // One shared "unread updates" flag for every button that shows it.
  const unreadUpdates = useUnreadUpdatesTracker(changelogApi)
  // Rebuilding the widget makes it download updates again (it only fetches
  // on start); the new instance re-publishes its API, which re-runs the
  // unread check.
  const changelogRefreshKey = useChangelogRefreshKey()
  const appEnv = getAppEnv()

  if (!appId || !isFeaturebaseEnabled(appEnv)) {
    return <>{children}</>
  }

  return (
    <FeaturebaseProvider
      appId={appId}
      featurebaseJwt={jwtResponse?.jwt}
      hideDefaultLauncher
    >
      {isFeaturebaseFeedbackEnabled(appEnv) && <FeedbackWidgetMount />}
      {/* Wait for the JWT (or its failure) so the widget isn't built once
          anonymously and then rebuilt for the signed-in engineer. */}
      {isJwtFetched && (
        <ChangelogWidgetMount
          key={changelogRefreshKey}
          onReady={setChangelogApi}
        />
      )}
      {isJwtFetched && <SurveyWidgetMount />}
      <FeaturebaseAvailableContext.Provider value={true}>
        <FeaturebaseChangelogContext.Provider value={changelogApi}>
          <FeaturebaseUnreadUpdatesContext.Provider value={unreadUpdates}>
            {children}
          </FeaturebaseUnreadUpdatesContext.Provider>
        </FeaturebaseChangelogContext.Provider>
      </FeaturebaseAvailableContext.Provider>
    </FeaturebaseProvider>
  )
}
